import { ADA_RESOURCES } from "@amzn/client-workbench-test-utils/dist/lib/ada-resources";
import { Auth } from "@aws-amplify/auth";

import { Logger } from "@/apis/Logger";
import { STAGE } from "@/helpers/stageConfig";

import type { ChatbotRequest, ChatbotResponse } from "../@types/agentcore";

/**
 * AgentCore chatbot runtime endpoint configuration per stage.
 * The runtime ARN is URL-encoded in the path.
 */
const CHATBOT_RUNTIME_CONFIG: Record<
  string,
  { region: string; runtimeArn: string }
> = {
  DEV: {
    region: "us-west-2",
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    runtimeArn: ADA_RESOURCES.BedrockAgentCore.EZO_CHATBOT_RUNTIME_ARN,
  },
  BETA: {
    region: "us-west-2",
    runtimeArn:
      "arn:aws:bedrock-agentcore:us-west-2:878084162945:runtime/ezo_chatbot-155dY98cGj",
  },
  GAMMA: {
    region: "us-west-2",
    runtimeArn:
      "arn:aws:bedrock-agentcore:us-west-2:106143622186:runtime/ezo_chatbot-RId8Qu3Hq1",
  },
  PROD: {
    region: "us-west-2",
    runtimeArn:
      "arn:aws:bedrock-agentcore:us-west-2:666185136605:runtime/ezo_chatbot-LLKqLA6v0w",
  },
};

const getEndpointUrl = (): string => {
  const config = CHATBOT_RUNTIME_CONFIG[STAGE];
  if (!config || !config.runtimeArn) {
    throw new Error(`Chatbot runtime ARN not configured for stage: ${STAGE}`);
  }
  const encodedArn = encodeURIComponent(config.runtimeArn);
  return `https://bedrock-agentcore.${config.region}.amazonaws.com/runtimes/${encodedArn}/invocations`;
};

const getTokens = async (): Promise<{
  accessToken: string;
  idToken: string;
}> => {
  const session = await Auth.currentSession();
  return {
    accessToken: session.getAccessToken().getJwtToken(),
    idToken: session.getIdToken().getJwtToken(),
  };
};

/**
 * Send a message to the chatbot AgentCore runtime.
 */
export const sendChatbotMessage = async (
  request: Omit<ChatbotRequest, "user_jwt">
): Promise<ChatbotResponse> => {
  const { accessToken, idToken } = await getTokens();

  const body: ChatbotRequest = {
    ...request,
    user_jwt: idToken,
  };

  const response = await fetch(getEndpointUrl(), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      "X-Amzn-Bedrock-AgentCore-Runtime-Session-Id": request.session_id,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorText = await response.text();
    void Logger.error(`Chatbot API error ${response.status}: ${errorText}`);
    throw new Error(`Chatbot request failed: ${response.status}`);
  }

  return (await response.json()) as ChatbotResponse;
};
